// Usage & Cost Dashboard (Usage & Cost Tracking, v2).
// Role-aware: Super Users see usage for their assigned projects (no server/cost);
// Admins additionally see cost, server runtime, an all|assigned scope toggle, and
// the login-sessions table. Everything reads the GET /api/usage/* endpoints; role
// and project scope are enforced server-side (this component only shapes the UI).
//
// v2: source toggle (RSA | Visualization | Both) wired to sources=, multi-project
// picker with name search, Names|IDs display toggle, user contains/excludes filter,
// collapsible sections, run-grouped analyses table with an honest Compute column
// (jobs — never "servers" unless instance ids were recorded), visualization
// graphs, and a Create-report dialog (summary / by-project / by-user detail).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { authHeaders } from '../../utils/authHeader';
import './UsageDashboard.css';

const PRESETS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const RDV_EXCLUDE = '@rdvsystems.';

const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
const SMALL_AXIS_TICK = { fill: '#94a3b8', fontSize: 10 };
const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};
const TOOLTIP_CURSOR = { fill: 'rgba(59, 130, 246, 0.08)' };
const LEGEND_STYLE = { color: '#cbd5e1' };

// Long project / user names are unreadable on a rotated x-axis, so the category
// bars are horizontal: the label lane is wide, left-aligned and never rotated.
// Values longer than the lane are ellipsised — the tooltip always shows the full
// name, so nothing is silently lost.
const CAT_LANE = 168;
const shortLabel = (v) => {
  const t = String(v ?? '—');
  return t.length > 26 ? `${t.slice(0, 25)}…` : t;
};
const catChartHeight = (rows) => Math.max(180, Math.min(460, (rows?.length || 0) * 28 + 48));

function fmtDuration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(); // browser-local from UTC (doc §14.4)
  } catch {
    return iso;
  }
}

async function getJson(url) {
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Collapsible section card; open/closed persisted per section in localStorage.
const SectionCard = ({ id, title, meta, defaultOpen = true, children }) => {
  const storageKey = `rsaUsage:section:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === null ? defaultOpen : saved === '1';
    } catch {
      return defaultOpen;
    }
  });
  const toggle = () => {
    setOpen((o) => {
      try { localStorage.setItem(storageKey, o ? '0' : '1'); } catch { /* ok */ }
      return !o;
    });
  };
  return (
    <div className="usage-section">
      <button type="button" className="usage-section-head" onClick={toggle}
        aria-expanded={open}>
        <span className={`usage-section-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="usage-section-title">{title}</span>
        {meta && <span className="usage-section-meta">{meta}</span>}
      </button>
      {open && <div className="usage-section-body">{children}</div>}
    </div>
  );
};

const UsageDashboard = () => {
  const { permissions, loading: permsLoading } = useUserPermissions();
  const perms = permissions?.permissions || [];
  const canViewCosts = perms.includes('view_costs');
  const canViewAllProjects = perms.includes('view_all_projects');
  const canViewSessions = perms.includes('view_usage_all');
  const canViewUsage = perms.includes('view_usage_assigned') || perms.includes('view_usage_all');

  const [source, setSource] = useState('both'); // rsa | visualization | both
  const [preset, setPreset] = useState('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userMode, setUserMode] = useState('contains'); // contains | excludes
  const [excludeRdv, setExcludeRdv] = useState(false);
  const [analysisType, setAnalysisType] = useState('');
  const [status, setStatus] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]); // ids
  const [projectSearch, setProjectSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState('name'); // name | id
  const [runId, setRunId] = useState('');
  const [scope, setScope] = useState('all'); // admin only
  const [serverType, setServerType] = useState(''); // admin only (§14.2)
  const [costMin, setCostMin] = useState('');       // admin only (§14.2)
  const [costMax, setCostMax] = useState('');       // admin only (§14.2)
  const [breakdownDim, setBreakdownDim] = useState('project');
  const [costDim, setCostDim] = useState('project');
  const [page, setPage] = useState(1);
  const [expandedRun, setExpandedRun] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveKind, setSaveKind] = useState('summary');
  const [saveRange, setSaveRange] = useState('previous_month');
  const [savedReports, setSavedReports] = useState([]);
  const [notice, setNotice] = useState('');
  const pageSize = 25;
  const pickerRef = useRef(null);

  const [options, setOptions] = useState({ analysis_types: [], server_types: [], projects: [] });
  const [summary, setSummary] = useState(null);
  const [analyses, setAnalyses] = useState({ rows: [], total: 0 });
  const [series, setSeries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [serverGraphs, setServerGraphs] = useState(null);
  const [costGraphs, setCostGraphs] = useState(null);
  const [visGraphs, setVisGraphs] = useState(null);
  const [sessions, setSessions] = useState({ rows: [], total: 0 });
  const [visSessions, setVisSessions] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const wantRsa = source !== 'visualization';
  const wantVis = source !== 'rsa';

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    // An explicit date/range wins over the preset buttons (same rule server-side).
    if (dateFrom || dateTo) {
      if (dateFrom) p.set('date_from', dateFrom);
      p.set('date_to', dateTo || dateFrom);   // single date => that whole day
    } else if (preset) {
      p.set('preset', preset);
    }
    if (userQuery.trim()) {
      p.set(userMode === 'excludes' ? 'user_exclude' : 'user_search', userQuery.trim());
    }
    if (excludeRdv) {
      const existing = p.get('user_exclude');
      p.set('user_exclude', existing ? `${existing},${RDV_EXCLUDE}` : RDV_EXCLUDE);
    }
    if (analysisType) p.set('analysis_type', analysisType);
    if (status) p.set('status', status);
    if (selectedProjects.length) p.set('projects', selectedProjects.join(','));
    if (runId.trim()) p.set('run_id', runId.trim());
    if (source !== 'both') p.set('sources', source);
    if (canViewAllProjects) p.set('scope', scope);
    if (canViewCosts) {
      if (serverType.trim()) p.set('server_type', serverType.trim());
      if (costMin !== '') p.set('cost_min', costMin);
      if (costMax !== '') p.set('cost_max', costMax);
    }
    return p.toString();
  }, [preset, dateFrom, dateTo, userQuery, userMode, excludeRdv, analysisType, status, selectedProjects,
      runId, source, scope, serverType, costMin, costMax, canViewAllProjects, canViewCosts]);

  const load = useCallback(async () => {
    if (!canViewUsage) return;
    setLoading(true);
    setError('');
    try {
      const summaryReq = getJson(`/api/usage/summary?${queryString}`);
      const rsaReqs = wantRsa ? [
        getJson(`/api/usage/analyses?${queryString}&page=${page}&page_size=${pageSize}`),
        getJson(`/api/usage/graphs/analyses-over-time?${queryString}`),
        getJson(`/api/usage/graphs/breakdown?${queryString}&dim=${breakdownDim}`),
        ...(canViewCosts ? [
          getJson(`/api/usage/graphs/server-usage?${queryString}`),
          getJson(`/api/usage/graphs/cost?${queryString}&dim=${costDim}`),
        ] : []),
      ] : [];
      const visReqs = wantVis ? [
        getJson(`/api/usage/vis-sessions?${queryString}&page=1&page_size=${pageSize}`),
        getJson(`/api/usage/graphs/vis?${queryString}`),
      ] : [];

      const [sum, ...rest] = await Promise.all([summaryReq, ...rsaReqs, ...visReqs]);
      setSummary(sum.summary || {});
      let i = 0;
      if (wantRsa) {
        const ana = rest[i++]; const gr = rest[i++]; const bd = rest[i++];
        setAnalyses({ rows: ana.rows || [], total: ana.total || 0 });
        setSeries(gr.series || []);
        setBreakdown(bd.series || []);
        if (canViewCosts) {
          setServerGraphs(rest[i++] || null);
          setCostGraphs(rest[i++] || null);
        }
      }
      if (wantVis) {
        const vis = rest[i++]; const vg = rest[i++];
        setVisSessions({ rows: vis.rows || [], total: vis.total || 0 });
        setVisGraphs(vg || null);
      }
      if (canViewSessions) {
        const ses = await getJson(`/api/usage/sessions?${queryString}&page=1&page_size=${pageSize}`);
        setSessions({ rows: ses.rows || [], total: ses.total || 0 });
      }
    } catch (e) {
      setError(`Failed to load usage data: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [queryString, page, breakdownDim, costDim, wantRsa, wantVis,
      canViewUsage, canViewSessions, canViewCosts]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canViewUsage) return;
    getJson('/api/usage/filter-options')
      .then((d) => setOptions({
        analysis_types: d.analysis_types || [],
        server_types: d.server_types || [],
        projects: d.projects || [],
      }))
      .catch(() => {});
  }, [canViewUsage]);

  // Reset to page 1 when filters change; close the picker on outside click.
  useEffect(() => { setPage(1); setExpandedRun(null); }, [queryString]);
  useEffect(() => {
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const projectLabel = useCallback((projectId, fallbackName) => {
    if (projectId == null) return '—';
    if (displayMode === 'id') return `#${projectId}`;
    const hit = options.projects.find((p) => p.id === String(projectId));
    return hit ? hit.name : (fallbackName || `Project ${projectId}`);
  }, [displayMode, options.projects]);

  const filteredPickerProjects = useMemo(() => {
    const frag = projectSearch.trim().toLowerCase();
    if (!frag) return options.projects;
    return options.projects.filter(
      (p) => p.name.toLowerCase().includes(frag) || p.id.includes(frag));
  }, [options.projects, projectSearch]);

  const toggleProject = (id) => setSelectedProjects((sel) =>
    sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);

  // Current dashboard state → a saved report definition. The date RANGE MODE is
  // saved (not the dates), so a schedule using this report always covers the
  // right period: 'previous_month' sends July on Aug 1, August on Sep 1.
  const currentFilters = useCallback(() => {
    const f = {};
    if (selectedProjects.length) f.projects = selectedProjects;
    if (userQuery.trim()) {
      const key = userMode === 'excludes' ? 'user_exclude' : 'user_search';
      f[key] = userMode === 'excludes' ? [userQuery.trim()] : userQuery.trim();
    }
    if (excludeRdv) {
      f.user_exclude = [...(f.user_exclude || []), RDV_EXCLUDE];
    }
    if (source !== 'both') f.sources = [source];
    if (analysisType) f.analysis_type = analysisType;
    if (status) f.status = status;
    if (canViewCosts) {
      if (serverType.trim()) f.server_type = serverType.trim();
      if (costMin !== '') f.cost_min = Number(costMin);
      if (costMax !== '') f.cost_max = Number(costMax);
    }
    return f;
  }, [selectedProjects, userQuery, userMode, excludeRdv, source, analysisType,
      status, serverType, costMin, costMax, canViewCosts]);

  const loadSavedReports = useCallback(() => {
    getJson('/api/usage/reports')
      .then((d) => setSavedReports(d.reports || []))
      .catch(() => {});
  }, []);

  useEffect(() => { if (canViewUsage) loadSavedReports(); }, [canViewUsage, loadSavedReports]);

  const saveReport = useCallback(async () => {
    if (!saveName.trim()) { setError('Give the report a name'); return; }
    try {
      const res = await fetch('/api/usage/reports', {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: saveName.trim(),
          report_type: canViewCosts && saveKind !== 'by_user_detail' ? 'cost' : 'usage',
          report_kind: saveKind,
          project_scope: canViewAllProjects ? scope : 'assigned',
          range_mode: saveRange,
          filters: currentFilters(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setNotice(`Report "${saveName.trim()}" saved — schedule it from Scheduled Reports.`);
      setSaveOpen(false);
      setSaveName('');
      loadSavedReports();
    } catch (e) {
      setError(`Could not save report: ${e.message}`);
    }
  }, [saveName, saveKind, saveRange, scope, canViewCosts, canViewAllProjects,
      currentFilters, loadSavedReports]);

  // Load a saved report's filters back into the dashboard.
  const applySavedReport = useCallback((rep) => {
    const f = rep.filters || {};
    setSelectedProjects(f.projects || []);
    setAnalysisType(f.analysis_type || '');
    setStatus(f.status || '');
    setServerType(f.server_type || '');
    setCostMin(f.cost_min ?? '');
    setCostMax(f.cost_max ?? '');
    setSource((f.sources && f.sources[0]) || 'both');
    const excl = f.user_exclude || [];
    setExcludeRdv(excl.includes(RDV_EXCLUDE));
    const otherExcl = excl.filter((x) => x !== RDV_EXCLUDE);
    if (otherExcl.length) { setUserMode('excludes'); setUserQuery(otherExcl[0]); }
    else if (f.user_search) { setUserMode('contains'); setUserQuery(f.user_search); }
    else { setUserQuery(''); }
    setNotice(`Loaded "${rep.name}" (${rep.range_mode.replace(/_/g, ' ')})`);
  }, []);

  const downloadPdf = useCallback(async (reportType, reportKind) => {
    try {
      const kindParam = reportKind && reportKind !== 'summary'
        ? `&report_kind=${reportKind}` : '';
      const res = await fetch(
        `/api/usage/report.pdf?${queryString}&report_type=${reportType}${kindParam}`,
        { headers: await authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-report${reportKind && reportKind !== 'summary' ? `-${reportKind}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setReportOpen(false);
    } catch (e) {
      setError(`Report download failed: ${e.message}`);
    }
  }, [queryString]);

  if (permsLoading) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-loading">
            <div className="usage-loading-spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }
  if (!canViewUsage) {
    return (
      <div className="usage-dashboard">
        <div className="usage-content usage-state">
          <div className="usage-denied"><h2>Access Denied</h2>
            <p>You don't have permission to view the usage dashboard.</p></div>
        </div>
      </div>
    );
  }

  const currency = summary?.currency || 'USD';
  const totalPages = Math.max(1, Math.ceil((analyses.total || 0) / pageSize));

  return (
    <div className="usage-dashboard">
      <main className="usage-content">
      <div className="usage-header">
        <h1>Usage {canViewCosts ? '& Cost ' : ''}Dashboard</h1>
        <div className="usage-header-controls">
          <div className="usage-source-toggle" role="tablist">
            {[['rsa', 'RSA'], ['visualization', 'Visualization'], ['both', 'Both']].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={source === k}
                className={source === k ? 'active' : ''}
                onClick={() => setSource(k)}>{label}</button>
            ))}
          </div>
          {canViewAllProjects && (
            <div className="usage-scope">
              <label>Scope:&nbsp;</label>
              <select value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="all">All Projects</option>
                <option value="assigned">Assigned Projects</option>
              </select>
            </div>
          )}
          <button type="button" className="usage-filter-action usage-filter-pdf"
            onClick={() => setReportOpen(true)}>Create report</button>
        </div>
      </div>

      <div className="usage-overview-row">
        <div className="usage-tiles">
          {wantRsa && <Tile label="Analyses" value={summary?.analyses_total ?? '—'} />}
          {wantRsa && <Tile label="Successful" value={summary?.analyses_successful ?? '—'} />}
          {wantRsa && <Tile label="Unsuccessful" value={summary?.analyses_unsuccessful ?? '—'} />}
          {wantVis && <Tile label="Viz sessions" value={summary?.vis_sessions_total ?? '—'} />}
          {wantVis && <Tile label="Viewing time"
            value={fmtDuration(summary?.vis_viewing_seconds)} />}
          {wantVis && <Tile label="Unique viewers" value={summary?.vis_unique_users ?? '—'} />}
          {canViewSessions && <Tile label="Login sessions" value={sessions.total ?? '—'} />}
          {wantRsa && canViewCosts && (
            <Tile label="Compute runtime"
              value={fmtDuration(summary?.total_server_runtime_seconds)} />
          )}
          {wantVis && canViewCosts && (
            <Tile label="Viewer runtime"
              value={fmtDuration(summary?.vis_infra_seconds)}
              hint="viewer machines' up-time; concurrent viewers count once" />
          )}
          {canViewCosts && (
            <Tile label="Total cost"
              value={summary?.total_cost_status === 'not_configured'
                ? 'Cost not configured'
                : (summary?.total_cost != null ? `${currency} ${summary.total_cost}` : '—')}
              hint={wantRsa && wantVis && summary?.vis_cost != null
                ? `analyses ${currency} ${(Math.round(((summary.total_cost || 0) - summary.vis_cost) * 100) / 100)} + viewers ${currency} ${summary.vis_cost}`
                : undefined} />
          )}
        </div>

        <div className="usage-filters">
          <div className="preset-group">
            {PRESETS.map((p) => (
              <button key={p.key} type="button"
                className={`usage-filter-preset ${(!dateFrom && !dateTo && preset === p.key) ? 'active' : ''}`}
                onClick={() => { setPreset(p.key); setDateFrom(''); setDateTo(''); }}>{p.label}</button>
            ))}
          </div>

          <div className="usage-daterange">
            <label>From
              <input type="date" className="usage-filter-control usage-date-input"
                value={dateFrom} max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label>To
              <input type="date" className="usage-filter-control usage-date-input"
                value={dateTo} min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)} />
            </label>
            {(dateFrom || dateTo) && (
              <button type="button" className="usage-date-clear"
                title="Back to the preset ranges"
                onClick={() => { setDateFrom(''); setDateTo(''); }}>clear</button>
            )}
            {dateFrom && !dateTo && (
              <span className="usage-muted usage-date-hint">single day</span>
            )}
          </div>

          <div className="usage-project-picker" ref={pickerRef}>
            <button type="button" className="usage-filter-control usage-filter-select"
              onClick={() => setPickerOpen((o) => !o)}>
              {selectedProjects.length === 0
                ? 'All projects'
                : `${selectedProjects.length} project${selectedProjects.length > 1 ? 's' : ''} selected`} ▾
            </button>
            {pickerOpen && (
              <div className="usage-picker-panel">
                <div className="usage-picker-toolbar">
                  <input type="text" placeholder='Search name or id (e.g. "GP")'
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)} />
                  <button type="button" disabled={!projectSearch.trim()}
                    onClick={() => setSelectedProjects((sel) => Array.from(new Set(
                      [...sel, ...filteredPickerProjects.map((p) => p.id)])))}>
                    Select matches
                  </button>
                  <button type="button" disabled={!selectedProjects.length}
                    onClick={() => setSelectedProjects([])}>Clear</button>
                  <span className="usage-picker-display">
                    Display:
                    <button type="button" className={displayMode === 'name' ? 'active' : ''}
                      onClick={() => setDisplayMode('name')}>Names</button>
                    <button type="button" className={displayMode === 'id' ? 'active' : ''}
                      onClick={() => setDisplayMode('id')}>IDs</button>
                  </span>
                </div>
                <div className="usage-picker-list">
                  {filteredPickerProjects.map((p) => (
                    <label key={p.id} className="usage-picker-item">
                      <input type="checkbox" checked={selectedProjects.includes(p.id)}
                        onChange={() => toggleProject(p.id)} />
                      <span>{p.name}</span>
                      <span className="usage-picker-id">#{p.id}</span>
                    </label>
                  ))}
                  {filteredPickerProjects.length === 0 && (
                    <div className="usage-empty">No projects match.</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="usage-user-filter">
            <select className="usage-filter-control usage-filter-select usage-narrow"
              value={userMode} onChange={(e) => setUserMode(e.target.value)}>
              <option value="contains">User contains</option>
              <option value="excludes">User excludes</option>
            </select>
            <input type="text" className="usage-filter-control usage-filter-input"
              placeholder="e.g. southernco.com"
              value={userQuery} onChange={(e) => setUserQuery(e.target.value)} />
            <button type="button"
              className={`usage-chip ${excludeRdv ? 'active' : ''}`}
              title="Exclude all @rdvsystems.com users"
              onClick={() => setExcludeRdv((v) => !v)}>
              {excludeRdv ? '✓ ' : ''}Exclude RDV
            </button>
          </div>

          <input type="text" className="usage-filter-control usage-filter-input"
            placeholder="Run ID"
            value={runId} onChange={(e) => setRunId(e.target.value)} />
          {wantRsa && (
            <select className="usage-filter-control usage-filter-select"
              value={analysisType} onChange={(e) => setAnalysisType(e.target.value)}>
              <option value="">All types</option>
              {options.analysis_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {wantRsa && (
            <select className="usage-filter-control usage-filter-select"
              value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="successful">Successful</option>
              <option value="unsuccessful">Unsuccessful</option>
              <option value="running">Running</option>
            </select>
          )}
          {wantRsa && canViewCosts && (
            <>
              <select className="usage-filter-control usage-filter-select usage-narrow"
                value={serverType} onChange={(e) => setServerType(e.target.value)}>
                <option value="">Any server</option>
                {options.server_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="number" placeholder="Cost ≥"
                className="usage-filter-control usage-filter-input usage-narrow" min="0" step="0.01"
                value={costMin} onChange={(e) => setCostMin(e.target.value)} />
              <input type="number" placeholder="Cost ≤"
                className="usage-filter-control usage-filter-input usage-narrow" min="0" step="0.01"
                value={costMax} onChange={(e) => setCostMax(e.target.value)} />
            </>
          )}
          <button type="button" className="usage-filter-action usage-filter-refresh"
            onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <div className="usage-error">{error}</div>}

      {reportOpen && (
        <div className="usage-report-dialog">
          <div className="usage-report-box">
            <h3>Create report now</h3>
            <p className="usage-muted">Uses the current filters, scope and source
              ({source}). Each report states its filters on the first page.</p>
            <div className="usage-report-kinds">
              <button type="button" onClick={() => downloadPdf(canViewCosts ? 'cost' : 'usage', 'summary')}>
                Summary report
              </button>
              <button type="button" onClick={() => downloadPdf(canViewCosts ? 'cost' : 'usage', 'by_project')}>
                By-project rollup
              </button>
              <button type="button" onClick={() => downloadPdf('usage', 'by_user_detail')}>
                By-user detail
              </button>
              {canViewCosts && (
                <button type="button" onClick={() => downloadPdf('usage', 'summary')}>
                  Summary (no cost)
                </button>
              )}
            </div>
            <div className="usage-report-divider" />
            <button type="button" className="usage-report-save"
              onClick={() => { setReportOpen(false); setSaveOpen(true); }}>
              Save as a report… <span className="usage-muted">(to schedule it)</span>
            </button>
            <button type="button" className="usage-report-close"
              onClick={() => setReportOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {saveOpen && (
        <div className="usage-report-dialog">
          <div className="usage-report-box">
            <h3>Save as a report</h3>
            <p className="usage-muted">Saves the current filters, scope and source
              so it can be scheduled. The period is saved as a <b>rule</b>, so a
              recurring send always covers the right window.</p>
            <label className="usage-save-field">
              Name
              <input type="text" value={saveName} autoFocus
                placeholder="e.g. GP projects — customer usage"
                onChange={(e) => setSaveName(e.target.value)} />
            </label>
            <label className="usage-save-field">
              Report kind
              <select value={saveKind} onChange={(e) => setSaveKind(e.target.value)}>
                <option value="summary">Summary</option>
                <option value="by_project">By-project rollup</option>
                <option value="by_user_detail">By-user detail</option>
              </select>
            </label>
            <label className="usage-save-field">
              Period
              <select value={saveRange} onChange={(e) => setSaveRange(e.target.value)}>
                <option value="previous_month">Previous calendar month</option>
                <option value="previous_week">Previous week (Mon–Sun)</option>
                <option value="previous_quarter">Previous quarter</option>
                <option value="previous_year">Previous year</option>
                <option value="month_to_date">Month to date</option>
                <option value="rolling_days">Rolling 30 days</option>
              </select>
            </label>
            <p className="usage-muted usage-save-hint">
              {saveRange === 'previous_month'
                ? 'Sent on the 1st of a month, this covers the whole previous month (Aug 1 → July).'
                : 'Resolved fresh at every send.'}
            </p>
            <div className="usage-report-kinds">
              <button type="button" onClick={saveReport}>Save report</button>
            </div>
            <button type="button" className="usage-report-close"
              onClick={() => setSaveOpen(false)}>Cancel</button>
          </div>
        </div>
      )}

      {savedReports.length > 0 && (
        <div className="usage-saved-reports">
          <span className="usage-muted">Saved reports:</span>
          {savedReports.map((r) => (
            <button key={r.id} type="button" className="usage-chip"
              title={`${r.report_kind} · ${r.range_mode.replace(/_/g, ' ')}`}
              onClick={() => applySavedReport(r)}>{r.name}</button>
          ))}
        </div>
      )}

      {notice && <div className="usage-notice">{notice}</div>}

      {wantRsa && (
      <SectionCard id="rsa-graphs" title="RSA graphs"
        meta={`${summary?.analyses_total ?? 0} analyses`}>
      <div className="usage-chart-grid">
        <div className="usage-chart">
          <h3>Analyses over time</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
              <XAxis dataKey="date" tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Line type="monotone" dataKey="successful" stroke="#2563eb" name="Successful" dot={false} />
              <Line type="monotone" dataKey="unsuccessful" stroke="#dc2626" name="Unsuccessful" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="usage-chart">
          <h3 className="usage-chart-select-title">
            <span>Analyses by</span>
            <select className="usage-chart-title-select"
              value={breakdownDim} onChange={(e) => setBreakdownDim(e.target.value)}>
              <option value="project">project</option>
              <option value="run">run</option>
              <option value="user">user</option>
              <option value="type">analysis type</option>
            </select>
          </h3>
          <ResponsiveContainer width="100%" height={catChartHeight(breakdown)}>
            <BarChart data={breakdown} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
              <XAxis type="number" tick={SMALL_AXIS_TICK} />
              <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
              <Legend wrapperStyle={LEGEND_STYLE} />
              <Bar dataKey="successful" stackId="a" fill="#2563eb" name="Successful" />
              <Bar dataKey="unsuccessful" stackId="a" fill="#dc2626" name="Unsuccessful" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {canViewCosts && serverGraphs && (
          <div className="usage-chart">
            <h3>Server usage by type (hours)</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={(serverGraphs.by_server_type || []).map((r) => ({
                  key: r.key, hours: Math.round((r.duration_seconds / 3600) * 100) / 100,
                }))}
                layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis type="number" tick={SMALL_AXIS_TICK} />
              <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="hours" fill="#0f766e" name="Hours" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {canViewCosts && costGraphs && (
          <div className="usage-chart">
            <h3>Cost over time ({costGraphs.currency || 'USD'})</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={costGraphs.over_time || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis dataKey="date" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Line type="monotone" dataKey="cost" stroke="#7c3aed" name="Cost" dot={false} />
              </LineChart>
            </ResponsiveContainer>
            {costGraphs.unrated_seconds > 0 && (
              <p className="usage-muted usage-unrated">
                {fmtDuration(costGraphs.unrated_seconds)} of server time has no configured
                rate and is excluded (Cost not configured).
              </p>
            )}
          </div>
        )}

        {canViewCosts && costGraphs && (
          <div className="usage-chart">
            <h3 className="usage-chart-select-title">
              <span>Cost by</span>
              <select className="usage-chart-title-select"
                value={costDim} onChange={(e) => setCostDim(e.target.value)}>
                <option value="project">project</option>
                <option value="user">user</option>
                <option value="type">analysis type</option>
                <option value="server_type">server type</option>
              </select>
              <span>({costGraphs.currency || 'USD'})</span>
            </h3>
            <ResponsiveContainer width="100%" height={catChartHeight(costGraphs.by_dim)}>
              <BarChart data={costGraphs.by_dim || []} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                <XAxis type="number" tick={SMALL_AXIS_TICK} />
              <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                <Bar dataKey="cost" fill="#7c3aed" name="Cost" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      </SectionCard>
      )}

      {wantRsa && (
      <SectionCard id="rsa-analyses" title="RSA analyses"
        meta={`${analyses.total} runs`}>
        <table className="usage-table">
          <thead>
            <tr>
              <th>Date</th><th>User</th><th>Project</th><th>Run</th><th>Types</th>
              <th>Duration</th><th>Status</th>
              {canViewCosts && <><th>Compute</th><th>Cost</th></>}
            </tr>
          </thead>
          <tbody>
            {analyses.rows.map((r) => (
              <React.Fragment key={r.run_id}>
              <tr>
                <td>{fmtDateTime(r.start_time).split(',')[0]}</td>
                <td>{r.user_email || '—'}</td>
                <td>{projectLabel(r.project_id, r.project_name)}</td>
                <td>
                  <button type="button" className="usage-runlink" title={r.run_id}
                    onClick={() => setRunId(r.run_id)}>
                    {shortRunId(r.run_id)}
                  </button>
                </td>
                <td>
                  {(r.analyses || []).map((a) => (
                    <span key={a.type} className={`usage-type-chip ${a.status || ''}`}
                      title={`${a.type}: ${a.status}`}>{a.type}</span>
                  ))}
                </td>
                <td>{fmtDuration(r.duration_seconds)}</td>
                <td><StatusBadge row={r} /></td>
                {canViewCosts && (
                  <td>
                    <ComputeCell row={r}
                      expanded={expandedRun === r.run_id}
                      onToggle={() => setExpandedRun(
                        expandedRun === r.run_id ? null : r.run_id)} />
                  </td>
                )}
                {canViewCosts && <td>{renderCost(r, currency)}</td>}
              </tr>
              {canViewCosts && expandedRun === r.run_id && (r.servers || []).length > 0 && (
                <tr className="usage-compute-detail">
                  <td colSpan={9}>
                    {(r.servers || []).map((s, i) => (
                      <div key={i} className="usage-compute-line">
                        {s.service} — {s.instances != null
                          ? `${s.instances} server${s.instances > 1 ? 's' : ''}`
                          : `${s.jobs} job${s.jobs > 1 ? 's' : ''}`} on {s.server_type}
                        {' · '}{fmtDuration(s.duration_seconds)}
                        {s.cost != null && ` · ${currency} ${s.cost}`}
                      </div>
                    ))}
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {analyses.rows.length === 0 && !loading && (
              <tr><td colSpan={canViewCosts ? 9 : 7} className="usage-empty">No analyses match the filters.</td></tr>
            )}
          </tbody>
        </table>
        <div className="usage-pager">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {totalPages} ({analyses.total} runs)</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </SectionCard>
      )}

      {wantVis && (
      <SectionCard id="visualization" title="Visualization"
        meta={`${summary?.vis_sessions_total ?? visSessions.total} sessions · ${fmtDuration(summary?.vis_viewing_seconds)}`}>
        {visGraphs && (
          <div className="usage-chart-grid">
            <div className="usage-chart">
              <h3>Viewer sessions over time</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={visGraphs.over_time || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                  <XAxis dataKey="date" tick={AXIS_TICK} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Line type="monotone" dataKey="sessions" stroke="#2563eb" name="Sessions" dot={false} />
                  <Line type="monotone" dataKey="hours" stroke="#0f766e" name="Hours" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="usage-chart">
              <h3>Viewing hours by project</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={(visGraphs.by_project || []).map((r) => ({
                    ...r, key: displayMode === 'id' ? `#${r.key}` : projectLabel(r.key),
                  }))}
                  layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                  <XAxis type="number" tick={SMALL_AXIS_TICK} />
              <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="hours" fill="#0f766e" name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="usage-chart">
              <h3>Viewing hours by user</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={visGraphs.by_user || []} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                  <XAxis type="number" tick={SMALL_AXIS_TICK} />
              <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="hours" fill="#7c3aed" name="Hours" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {canViewCosts && visGraphs.cost && (
              <>
                <div className="usage-chart">
                  <h3>Viewer usage by machine type</h3>
                  <ResponsiveContainer width="100%" height={catChartHeight(visGraphs.cost.by_type)}>
                    <BarChart data={(visGraphs.cost.by_type || []).map((r) => ({
                      key: r.key, hours: Math.round((r.duration_seconds / 3600) * 100) / 100,
                    }))} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                      <XAxis type="number" tick={SMALL_AXIS_TICK} />
                      <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                        tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                      <Bar dataKey="hours" fill="#0ea5e9" name="Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="usage-chart">
                  <h3>Viewer cost over time ({currency})</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={visGraphs.cost.over_time || []}
                      margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                      <XAxis dataKey="date" tick={AXIS_TICK} />
                      <YAxis tick={AXIS_TICK} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                      <Line type="monotone" dataKey="cost" stroke="#0ea5e9" name="Cost" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="usage-chart">
                  <h3>Viewer cost by project ({currency})</h3>
                  <ResponsiveContainer width="100%" height={catChartHeight(visGraphs.cost.by_project)}>
                    <BarChart data={visGraphs.cost.by_project || []} layout="vertical"
                      margin={{ top: 4, right: 24, bottom: 4, left: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--usage-grid, #e2e8f0)" />
                      <XAxis type="number" tick={SMALL_AXIS_TICK} />
                      <YAxis type="category" dataKey="key" width={CAT_LANE} interval={0}
                        tick={SMALL_AXIS_TICK} tickFormatter={shortLabel} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                      <Bar dataKey="cost" fill="#0ea5e9" name="Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                  <p className="usage-muted usage-unrated">
                    Priced from each project's viewer machine type and the same rate card
                    as analyses. Overlapping sessions count once — the machine is shared.
                    {(visGraphs.cost.unpriced_projects || []).length > 0 && (
                      <> {visGraphs.cost.unpriced_projects.length} project(s) have no configured
                      rate and are excluded (Cost not configured).</>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>
        )}
        <table className="usage-table">
          <thead>
            <tr><th>User</th><th>Project</th><th>Start</th><th>End</th><th>Duration</th></tr>
          </thead>
          <tbody>
            {visSessions.rows.map((s, i) => (
              <tr key={i}>
                <td>{s.user_email}</td>
                <td>{projectLabel(s.project_id)}</td>
                <td>{fmtDateTime(s.login_time)}</td>
                <td>{s.active ? <em>active</em> : fmtDateTime(s.logout_time)}</td>
                <td>{s.active ? '—' : fmtDuration(s.duration_seconds)}</td>
              </tr>
            ))}
            {visSessions.rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="usage-empty">No visualization sessions match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </SectionCard>
      )}

      {canViewSessions && (
      <SectionCard id="login-sessions" title="Login sessions"
        meta={`${sessions.total} sessions`} defaultOpen={false}>
        <table className="usage-table">
          <thead>
            <tr><th>User</th><th>Login</th><th>Logout</th><th>Duration</th><th>Reason</th></tr>
          </thead>
          <tbody>
            {sessions.rows.map((s, i) => (
              <tr key={i}>
                <td>{s.user_email}</td>
                <td>{fmtDateTime(s.login_time)}</td>
                <td>{s.active ? <em>active</em> : fmtDateTime(s.logout_time)}</td>
                <td>{s.active ? '—' : fmtDuration(s.login_duration_seconds)}</td>
                <td>{s.logout_reason || '—'}</td>
              </tr>
            ))}
            {sessions.rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="usage-empty">No sessions match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </SectionCard>
      )}
      </main>
    </div>
  );
};

const Tile = ({ label, value, hint }) => (
  <div className="usage-tile">
    <div className="tile-value">{value}</div>
    <div className="tile-label">{label}</div>
    {hint && <div className="usage-tile-hint">{hint}</div>}
  </div>
);

const StatusBadge = ({ row }) => {
  const s = row.status;
  const cls = s === 'successful' ? 'ok' : s === 'unsuccessful' ? 'bad' : 'run';
  const failed = (row.analyses || []).find((a) => a.status === 'unsuccessful');
  return (
    <span className={`usage-status ${cls}`}>
      {s}
      {s === 'unsuccessful' && (failed?.log_ref
        ? <span className="usage-loghint" title={`log: ${failed.log_ref.run_id || ''}`}> ⓘ</span>
        : <span className="usage-loghint" title="Log file not available"> —</span>)}
    </span>
  );
};

// Honest compute summary: never says "servers" unless instance ids were
// recorded for every usage row (Batch packs several jobs onto one instance,
// so a job count is NOT a server count).
const ComputeCell = ({ row, expanded, onToggle }) => {
  const summ = row.compute_summary;
  if (row.cost_status === 'not_available' || !summ) {
    return <span className="usage-muted">Server usage not available</span>;
  }
  const head = summ.instances != null
    ? `${summ.instances} server${summ.instances > 1 ? 's' : ''}`
    : `${summ.jobs} job${summ.jobs > 1 ? 's' : ''}`;
  return (
    <button type="button" className="usage-compute-toggle" onClick={onToggle}
      title={expanded ? 'Hide breakdown' : 'Show per-service breakdown'}>
      Σ {fmtDuration(summ.total_seconds)} · {head} · {summ.server_types} type{summ.server_types > 1 ? 's' : ''}
      <span className={`usage-section-chevron ${expanded ? 'open' : ''}`}> ▸</span>
    </button>
  );
};

// "project_2442_run_pyexisting_280726-0341-2b6e" → "280726-0341-2b6e"
function shortRunId(runId) {
  if (!runId) return '—';
  const m = String(runId).match(/run_(?:\w+_)?(.+)$/);
  return m ? m[1] : runId;
}

function renderCost(r, currency) {
  if (r.cost_status === 'not_available') return <span className="usage-muted">—</span>;
  if (r.cost_status === 'not_configured') return <span className="usage-muted">Cost not configured</span>;
  if (r.total_cost == null) return '—';
  return `${currency} ${r.total_cost}`;
}

export default UsageDashboard;
